import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "text-h6" }
const _hoisted_2 = { class: "form-group mb-3" }
const _hoisted_3 = { class: "d-block mb-1 small text-muted" }
const _hoisted_4 = { class: "form-group mb-3" }
const _hoisted_5 = { class: "d-block mb-1 small text-muted" }
const _hoisted_6 = { class: "text-caption text-muted q-mt-xs" }
const _hoisted_7 = { class: "form-group mb-3" }
const _hoisted_8 = { class: "d-block mb-1 small text-muted" }
const _hoisted_9 = { class: "text-caption text-muted q-mt-xs" }
const _hoisted_10 = {
  key: 0,
  class: "form-group mb-3"
}
const _hoisted_11 = { class: "d-block mb-1 small text-muted" }
const _hoisted_12 = { class: "template-sender__attachments" }
const _hoisted_13 = { class: "form-group mb-0" }
const _hoisted_14 = { class: "d-block mb-1 small text-muted" }

import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import Notify from 'quasar/src/plugins/Notify.js';;
import store from "@/store";
import { initElectronicSign } from "@/commons/eSign";
import { getMail } from "@/store/services/task/actions/printDocumentService";
import { Template } from "@/types";
import { getEntity } from "@/utils";
import i18n from "@/i18n";
import {
  formatEmailBodyForTemplate,
  getAgencyEmailFromOffer,
  getAllRecipientEmails,
  isValidEmail,
  parseJsonField,
  STUDIES_TEAM_EMAIL,
  syncCcWithRecipients,
  TEMPLATE_NOTIF_SM,
} from "@/utils/emailForm";
import { buildPrintDocumentDefaultBody, getEmailHeaderInfo } from "@/utils/emailHeaderInfo";
import type { DocumentEmailSendOptions } from "@/store/services/task/actions/printDocumentService";

const ATTACHMENTS_PREVIEW_LIMIT = 4;

export default /*@__PURE__*/_defineComponent({
  __name: 'TemplateSender',
  setup(__props) {

const route = useRoute();

const selectedRecipients = ref<string[]>([]);
const otherEmailsList = ref<string[]>([]);
const ccEmailsList = ref<string[]>([]);
const emailBody = ref("");
const recipientOptions = ref<{ value: string; label: string }[]>([]);
const isLoadingSend = ref(false);

const templateSelected = computed(() => {
  const fileList = store.getters["templateModule/getSelectedTemplates"]
    .filter((template: string) => {
      const templates = store.getters["templateModule/getTemplates"];
      return templates.some(
        (t: Template) =>
          t.processId === template &&
          t.signatureType === store.state.taskModule.selectedTask.variables.signatureMethod
      );
    })
    .sort((a: any, b: any) => {
      const templateA = store.getters["templateModule/getTemplates"].find(
        (t: any) => t.processId === a
      );
      const templateB = store.getters["templateModule/getTemplates"].find(
        (t: any) => t.processId === b
      );
      return (templateA?.documentOrder || 0) - (templateB?.documentOrder || 0);
    });
  return store.getters["templateModule/getTemplates"].filter((template: Template) =>
    fileList.includes(template.processId)
  );
});

const showAllAttachments = ref(false);

const displayedTemplates = computed(() => {
  const list = templateSelected.value;
  if (showAllAttachments.value || list.length <= ATTACHMENTS_PREVIEW_LIMIT) {
    return list;
  }
  return list.slice(0, ATTACHMENTS_PREVIEW_LIMIT);
});

const hiddenAttachmentsCount = computed(() => {
  const total = templateSelected.value.length;
  if (total <= ATTACHMENTS_PREVIEW_LIMIT) {
    return 0;
  }
  return total - ATTACHMENTS_PREVIEW_LIMIT;
});

const showAttachmentsToggle = computed(() => hiddenAttachmentsCount.value > 0);

watch(templateSelected, (newValue) => {
  if (!newValue || !newValue.length) {
    onClose();
  }
});

const buildRecipientOption = (email: string, roleLabelKey: string) => ({
  value: email,
  label: `${i18n.global.t(roleLabelKey)}: ${email}`,
});

const extractEmailFromContacts = (entity: any): string | null => {
  if (!entity || entity === "") return null;
  const contacts = entity.person?.contacts ?? entity.contacts ?? [];
  for (const contact of contacts) {
    if (contact?.contactMechanism?.type?.id === "EMAIL_CONTACT") {
      const email = contact.contactMechanism.emailAddress?.trim();
      if (email && isValidEmail(email)) {
        return email;
      }
    }
  }
  return null;
};

const loadInternalRecipients = async (offer: any) => {
  const options: { value: string; label: string }[] = [];

  if (offer?.assignedSalesPerson) {
    try {
      const user = await getEntity("odm-user", "leaseforgeuser", offer.assignedSalesPerson);
      const commercialEmail = user?.email?.trim();
      if (commercialEmail && isValidEmail(commercialEmail)) {
        options.push(
          buildRecipientOption(
            commercialEmail,
            "middleOffice.generalInfo.label.assignedSalesPerson"
          )
        );
      }
    } catch {
      // optional
    }
  }

  const salesManager = store.state.demandModule.salesManager;
  let managerEmail: string | null = null;
  if (salesManager && salesManager !== "") {
    managerEmail =
      (typeof salesManager.email === "string" ? salesManager.email.trim() : null) ||
      extractEmailFromContacts(salesManager);
  }
  if (!managerEmail) {
    const businessData = parseJsonField(offer?.businessData);
    const email = businessData?.salesManager?.email?.trim();
    if (email && isValidEmail(email)) {
      managerEmail = email;
    }
  }
  if (managerEmail) {
    options.push(
      buildRecipientOption(managerEmail, "middleOffice.generalInfo.label.managerPerson")
    );
  }

  return options;
};

const mergeRecipientOptions = (
  base: { value: string; label: string }[],
  extra: { value: string; label: string }[]
) => {
  const seen = new Set(base.map((item) => item.value.toLowerCase()));
  const merged = [...base];
  for (const item of extra) {
    const key = item.value.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(item);
    }
  }
  return merged;
};

const resolveMailContext = () => {
  const routeName = route.name as string;
  switch (routeName) {
    case "order-edit":
      return {
        entity: store.state.orderModule.order,
        entityType: "order" as const,
        offer: store.state.taskModule.offer ?? store.state.demandModule.offer,
      };
    case "TaskDetail": {
      const entityType = store.getters["taskModule/isTaskModuleOffer"] ? "offer" : "order";
      return {
        entity:
          entityType === "offer"
            ? store.state.taskModule.offer
            : store.state.taskModule.order,
        entityType,
        offer: store.state.taskModule.offer ?? store.state.demandModule.offer,
      };
    }
    case "demand-edit":
    case "mo-demand-detail":
      return {
        entity: store.state.demandModule.offer,
        entityType: "offer" as const,
        offer: store.state.demandModule.offer,
      };
    case "third-edit":
      return {
        entity: store.state.thirdModule.third,
        entityType: "party" as const,
        offer: store.state.taskModule.offer ?? store.state.demandModule.offer,
      };
    default:
      return {
        entity: store.state.taskModule.offer,
        entityType: "offer" as const,
        offer: store.state.taskModule.offer ?? store.state.demandModule.offer,
      };
  }
};

const loadDefaultCcEmails = async (): Promise<string[]> => {
  const { offer } = resolveMailContext();
  const ccList: string[] = [];
  const agencyEmail = offer ? getAgencyEmailFromOffer(offer) : null;
  if (agencyEmail) {
    ccList.push(agencyEmail);
  }
  ccList.push(STUDIES_TEAM_EMAIL);
  return [...new Set(ccList)];
};

const loadRecipients = async () => {
  const { entity, entityType, offer } = resolveMailContext();
  if (!entity) {
    recipientOptions.value = [];
    return;
  }
  try {
    const mailResponse = await getMail(entity, entityType);
    const fromParties = (mailResponse.items || [])
      .filter((item: any) => item.value && item.value !== "null")
      .map((item: any) => ({
        value: item.value,
        label: item.label || item.value,
      }));

    let internalRecipients: { value: string; label: string }[] = [];
    if (offer) {
      internalRecipients = await loadInternalRecipients(offer);
    }
    recipientOptions.value = mergeRecipientOptions(fromParties, internalRecipients);

    const commercialRecipient = internalRecipients.find((item) =>
      item.label.startsWith(
        i18n.global.t("middleOffice.generalInfo.label.assignedSalesPerson")
      )
    );
    if (commercialRecipient) {
      selectedRecipients.value = [commercialRecipient.value];
    }
  } catch (err: any) {
    recipientOptions.value = [];
    Notify.create({
      timeout: 8000,
      actions: [{ icon: "close", color: "white" }],
      message: err?.message || i18n.global.t("task.analysisOnHold.recipientsLoadError"),
      color: "negative",
    });
  }
};

const onNewEmail = (
  value: string,
  done: (item?: string, mode?: "add" | "add-unique" | "toggle") => void
) => {
  const email = value.trim();
  if (!email) {
    done();
    return;
  }
  if (!isValidEmail(email)) {
    Notify.create({
      timeout: 4000,
      message: i18n.global.t("task.analysisOnHold.invalidEmails"),
      color: "negative",
    });
    done();
    return;
  }
  const isAlreadyRecipient = selectedRecipients.value.some(
    (recipient) => recipient.toLowerCase() === email.toLowerCase()
  );
  if (isAlreadyRecipient) {
    Notify.create({
      timeout: 4000,
      message: i18n.global.t("task.analysisOnHold.emailAlreadyRecipient"),
      color: "negative",
    });
    done();
    return;
  }
  done(email, "add-unique");
};

const applyCcSync = () => {
  ccEmailsList.value = syncCcWithRecipients(
    ccEmailsList.value,
    selectedRecipients.value,
    otherEmailsList.value
  );
};

watch(selectedRecipients, () => {
  const recipientSet = new Set(selectedRecipients.value.map((email) => email.toLowerCase()));
  otherEmailsList.value = otherEmailsList.value.filter(
    (email) => !recipientSet.has(email.toLowerCase())
  );
  applyCcSync();
});

watch(otherEmailsList, () => {
  applyCcSync();
});

const resetForm = async () => {
  selectedRecipients.value = [];
  otherEmailsList.value = [];
  ccEmailsList.value = [];
  emailBody.value = buildPrintDocumentDefaultBody();
  await Promise.all([
    loadRecipients(),
    loadDefaultCcEmails().then((emails) => {
      ccEmailsList.value = emails;
    }),
  ]);
  applyCcSync();
};

const showSendTemplate = computed(
  () => !!store.state.printDocumentModule?.showSendTemplate
);

watch(showSendTemplate, (open) => {
  if (open) {
    showAllAttachments.value = false;
    resetForm();
  }
});

const onDialogUpdate = (open: boolean) => {
  store.dispatch("printDocumentModule/changeShowSendTemplate", open);
};

const onClose = () => {
  onDialogUpdate(false);
};

const sendMail = () => {
  const to = getAllRecipientEmails(selectedRecipients.value, otherEmailsList.value);
  const invalidOther = otherEmailsList.value.filter((email) => !isValidEmail(email));
  const invalidCc = ccEmailsList.value.filter((email) => !isValidEmail(email));

  if (invalidOther.length > 0) {
    Notify.create({
      timeout: 5000,
      message: i18n.global.t("task.analysisOnHold.invalidEmails"),
      color: "negative",
    });
    return;
  }
  if (invalidCc.length > 0) {
    Notify.create({
      timeout: 5000,
      message: i18n.global.t("task.analysisOnHold.invalidCcEmails"),
      color: "negative",
    });
    return;
  }
  if (to.length === 0) {
    Notify.create({
      timeout: 5000,
      message: i18n.global.t("task.analysisOnHold.recipientRequired"),
      color: "negative",
    });
    return;
  }

  const cc = ccEmailsList.value.filter((email) => isValidEmail(email));
  const { lizfiNumber, offerNumber, externalReference, clientName } = getEmailHeaderInfo();

  const mailSendOptions: DocumentEmailSendOptions = {
    to,
    cc,
    emailBodyHtml: formatEmailBodyForTemplate(emailBody.value),
    lizfiNumber,
    offerNumber,
    externalReference: externalReference || undefined,
    clientName,
    templateResourceUid: TEMPLATE_NOTIF_SM,
  };

  isLoadingSend.value = true;
  initElectronicSign(null, null, null, true, mailSendOptions).finally(() => {
    isLoadingSend.value = false;
  });
};

const canSend = computed(
  () => getAllRecipientEmails(selectedRecipients.value, otherEmailsList.value).length > 0
);

const isSending = computed(
  () => isLoadingSend.value || !!store.state.printDocumentModule?.isLoading
);

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_select = _resolveComponent("q-select")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_chip = _resolveComponent("q-chip")!
  const _component_q_input = _resolveComponent("q-input")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    "model-value": showSendTemplate.value,
    persistent: "",
    "onUpdate:modelValue": onDialogUpdate
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "analysis-on-hold-dialog" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "analysis-on-hold-dialog__header" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, _toDisplayString(_ctx.$t("task.printDocument.sentCTA")), 1),
              _createVNode(_component_q_btn, {
                flat: "",
                round: "",
                dense: "",
                icon: "close",
                class: "analysis-on-hold-dialog__close",
                "aria-label": _ctx.$t('task.buttons.cancel'),
                disable: isSending.value,
                onClick: onClose
              }, null, 8, ["aria-label", "disable"])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("label", _hoisted_3, _toDisplayString(_ctx.$t("task.printDocument.templateForwardDocuments.recipient")), 1),
                _createVNode(_component_q_select, {
                  modelValue: selectedRecipients.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedRecipients).value = $event)),
                  dense: "",
                  outlined: "",
                  multiple: "",
                  "use-chips": "",
                  "emit-value": "",
                  "map-options": "",
                  clearable: "",
                  class: "analysis-on-hold-dialog__select analysis-on-hold-dialog__email-select",
                  options: recipientOptions.value,
                  placeholder: 
              selectedRecipients.value.length ? '' : _ctx.$t('task.analysisOnHold.recipientPlaceholder')
            
                }, null, 8, ["modelValue", "options", "placeholder"])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("label", _hoisted_5, _toDisplayString(_ctx.$t("task.analysisOnHold.otherEmails")), 1),
                _createVNode(_component_q_select, {
                  modelValue: otherEmailsList.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((otherEmailsList).value = $event)),
                  dense: "",
                  outlined: "",
                  multiple: "",
                  "use-chips": "",
                  "use-input": "",
                  "hide-dropdown-icon": "",
                  "new-value-mode": "add-unique",
                  "input-debounce": "0",
                  class: "analysis-on-hold-dialog__email-select",
                  placeholder: 
              otherEmailsList.value.length ? '' : _ctx.$t('task.analysisOnHold.otherEmailsPlaceholder')
            ,
                  onNewValue: onNewEmail
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_q_icon, {
                      name: "alternate_email",
                      color: "grey-7",
                      size: "xs"
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "placeholder"]),
                _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t("task.analysisOnHold.otherEmailsHint")), 1)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("label", _hoisted_8, _toDisplayString(_ctx.$t("task.analysisOnHold.ccEmails")), 1),
                _createVNode(_component_q_select, {
                  modelValue: ccEmailsList.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((ccEmailsList).value = $event)),
                  dense: "",
                  outlined: "",
                  multiple: "",
                  "use-chips": "",
                  "use-input": "",
                  "hide-dropdown-icon": "",
                  "new-value-mode": "add-unique",
                  "input-debounce": "0",
                  class: "analysis-on-hold-dialog__email-select",
                  placeholder: ccEmailsList.value.length ? '' : _ctx.$t('task.analysisOnHold.ccEmailsPlaceholder'),
                  onNewValue: onNewEmail
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_q_icon, {
                      name: "content_copy",
                      color: "grey-7",
                      size: "xs"
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "placeholder"]),
                _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t("task.analysisOnHold.ccEmailsHint")), 1)
              ]),
              (templateSelected.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createElementVNode("label", _hoisted_11, _toDisplayString(_ctx.$t("task.printDocument.attachedDocuments")), 1),
                    _createElementVNode("div", _hoisted_12, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedTemplates.value, (template) => {
                        return (_openBlock(), _createBlock(_component_q_chip, {
                          key: template.processId,
                          dense: "",
                          outline: "",
                          color: "primary",
                          class: "template-sender__attachment-chip"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(template.fileName), 1)
                          ]),
                          _: 2
                        }, 1024))
                      }), 128))
                    ]),
                    (showAttachmentsToggle.value)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: "template-sender__attachments-toggle btn btn-link btn-sm p-0 q-mt-xs",
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (showAllAttachments.value = !showAllAttachments.value))
                        }, _toDisplayString(showAllAttachments.value
                ? _ctx.$t("task.printDocument.showLessAttachments")
                : _ctx.$t("task.printDocument.showMoreAttachments", { count: hiddenAttachmentsCount.value })), 1))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("label", _hoisted_14, _toDisplayString(_ctx.$t("task.analysisOnHold.messageBody")), 1),
                _createVNode(_component_q_input, {
                  modelValue: emailBody.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((emailBody).value = $event)),
                  type: "textarea",
                  dense: "",
                  outlined: "",
                  autogrow: "",
                  class: "analysis-on-hold-dialog__body"
                }, null, 8, ["modelValue"])
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_actions, {
            align: "right",
            class: "analysis-on-hold-dialog__actions"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_btn, {
                flat: "",
                label: _ctx.$t('task.buttons.cancel'),
                color: "negative",
                disable: isSending.value,
                onClick: onClose
              }, null, 8, ["label", "disable"]),
              _createVNode(_component_q_btn, {
                flat: "",
                label: _ctx.$t('task.printDocument.sentCTA'),
                color: "primary",
                loading: isSending.value,
                disable: isSending.value || !canSend.value,
                onClick: sendMail
              }, null, 8, ["label", "loading", "disable"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})